import { Modal } from '@marriott/mi-ui-library';
import React, { useEffect, useRef, useCallback } from 'react';
import { useNavContext } from '../../../organisms/NavigationContainer';
import { StyledMyTripsModal, StyledMyTripsModalCloseButton } from './MyTripsModal.styles';

export const MyTripsModal = () => {
  const { isMyTripsModalOpen, setIsMyTripsModalOpen } = useNavContext();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const setClosed = useCallback(() => {
    setIsMyTripsModalOpen(false);
    previousFocusRef.current?.focus();
  }, [setIsMyTripsModalOpen]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setClosed();
        return;
      }

      if (event.key === 'Tab' && modalContentRef.current) {
        const focusableElements = modalContentRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), iframe'
        );
        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          if (document.activeElement === firstFocusable) {
            event.preventDefault();
            lastFocusable?.focus();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            event.preventDefault();
            firstFocusable?.focus();
          }
        }
      }
    },
    [setClosed]
  );

  useEffect(() => {
    if (isMyTripsModalOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.addEventListener('keydown', handleKeyDown);
      setTimeout(() => closeButtonRef.current?.focus(), 100);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMyTripsModalOpen, handleKeyDown]);

  return (
    <StyledMyTripsModal
      modalId="mytripsmodal"
      show={isMyTripsModalOpen}
      handleBlur={true}
      popupOpenState={isMyTripsModalOpen}
      onClose={setClosed}
      className="my-trips-modal"
      secondaryClassName="my-trips-modal-inner"
      role="dialog"
      aria-modal={true}
      aria-label="My Trips"
    >
      <Modal.Body className="my-trips-modal-body">
        <div ref={modalContentRef} tabIndex={-1}>
          <StyledMyTripsModalCloseButton
            ref={closeButtonRef}
            className="icon-clear"
            onClick={setClosed}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setClosed();
              }
            }}
            type="button"
            aria-label="Close Dialog"
          >
            <span className="sr-only">Close Dialog</span>
          </StyledMyTripsModalCloseButton>
          <iframe
            src="/resLookupOverlay.mi?overlay=true"
            title="resLookupOverlay"
            frameBorder="0"
            allowFullScreen={false}
            id="resLookupOverlayIframe"
            width="100%"
            data-dt-last-src="https://www.marriott.com/resLookupOverlay.mi?overlay=true"
            style={{ height: '500px' }}
          />
        </div>
      </Modal.Body>
    </StyledMyTripsModal>
  );
};
